import { api } from '@/lib/api/client';
import type {
  Activity,
  ActivitiesResponse,
  ActivityFilters,
  StartActivityDto,
  StopActivityDto,
  ActivityType,
  ActivityTypesResponse,
  CreateActivityTypeDto,
  UpdateActivityTypeDto,
} from '../types';

// Query keys for activities
export const activityKeys = {
  all: ['activities'] as const,
  lists: () => [...activityKeys.all, 'list'] as const,
  list: (filters: ActivityFilters) => [...activityKeys.lists(), filters] as const,
  details: () => [...activityKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...activityKeys.details(), id] as const,
  active: () => [...activityKeys.all, 'active'] as const,
};

// Query keys for activity types
export const activityTypeKeys = {
  all: ['activity-types'] as const,
  lists: () => [...activityTypeKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...activityTypeKeys.lists(), filters] as const,
  details: () => [...activityTypeKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...activityTypeKeys.details(), id] as const,
};

// Build query params from filters
const buildActivityParams = (filters: ActivityFilters): Record<string, unknown> => {
  const offset = filters.page * filters.limit;
  const params: Record<string, unknown> = {
    limit: filters.limit,
    offset,
  };

  // Search
  if (filters.s && filters.s.length >= 2) {
    params['s'] = filters.s;
  }

  // Sorting
  if (filters.orderBy && filters.order) {
    params['orderBy'] = filters.orderBy;
    params['order'] = filters.order;
  }

  // Status
  if (filters.status) {
    params['status'] = filters.status;
  }

  // Non-billable filter
  if (filters.nonBillable !== undefined) {
    params['nonBillable'] = filters.nonBillable;
  }

  // Date filters
  if (filters['createdAt.min']) {
    const minDate = new Date(filters['createdAt.min']);
    minDate.setHours(0, 0, 0, 0);
    params['createdAt.min'] = minDate.toISOString();
  }

  if (filters['createdAt.max']) {
    const maxDate = new Date(filters['createdAt.max']);
    maxDate.setHours(23, 59, 59, 999);
    params['createdAt.max'] = maxDate.toISOString();
  }

  if (filters['date.min']) {
    params['date.min'] = new Date(filters['date.min']).toISOString();
  }

  if (filters['date.max']) {
    params['date.max'] = new Date(filters['date.max']).toISOString();
  }

  if (filters['lastStartTime.min']) {
    params['lastStartTime.min'] = new Date(filters['lastStartTime.min']).toISOString();
  }

  if (filters['lastStartTime.max']) {
    params['lastStartTime.max'] = new Date(filters['lastStartTime.max']).toISOString();
  }

  if (filters['lastEndTime.min']) {
    params['lastEndTime.min'] = new Date(filters['lastEndTime.min']).toISOString();
  }

  if (filters['lastEndTime.max']) {
    params['lastEndTime.max'] = new Date(filters['lastEndTime.max']).toISOString();
  }

  // ID filters
  if (filters.createdById) params['createdById'] = filters.createdById;
  if (filters.kpiId) params['kpiId'] = filters.kpiId;
  if (filters.typeId) params['typeId'] = filters.typeId;
  if (filters.billId) params['billId'] = filters.billId;
  if (filters.taskId) params['taskId'] = filters.taskId;
  if (filters.matterId) params['matterId'] = filters.matterId;
  if (filters.contractId) params['contractId'] = filters.contractId;
  if (filters.clientId) params['clientId'] = filters.clientId;

  return params;
};

// Activity API functions
export const activitiesApi = {
  // Get all activities with filters
  getAll: async (filters: ActivityFilters): Promise<ActivitiesResponse> => {
    const params = buildActivityParams(filters);
    return api.get<ActivitiesResponse>('/activities', { params });
  },

  // Get single activity
  getById: async (id: string | number): Promise<Activity> => {
    return api.get<Activity>(`/activities/${id}`);
  },

  // Get active activity
  getActive: async (): Promise<Activity | null> => {
    return api.get<Activity | null>('/activities/active');
  },

  // Start activity
  start: async (data: StartActivityDto): Promise<Activity> => {
    return api.post<Activity>('/activities/start', data);
  },

  // Stop activity
  stop: async (id: string | number, data?: StopActivityDto): Promise<Activity> => {
    return api.put<Activity>(`/activities/stop/${id}`, data);
  },

  // Change activity status
  changeStatus: async (url: string, data?: Record<string, unknown>): Promise<Activity> => {
    return api.put<Activity>(`/activities/${url}`, data);
  },

  // Delete activity
  delete: async (id: string | number): Promise<void> => {
    await api.delete(`/activities/${id}`);
  },
};

// Activity Types API functions
export const activityTypesApi = {
  // Get all activity types
  getAll: async (params?: Record<string, unknown>): Promise<ActivityTypesResponse> => {
    return api.get<ActivityTypesResponse>('/activity-types', { params });
  },

  // Get single activity type
  getById: async (id: string | number): Promise<ActivityType> => {
    return api.get<ActivityType>(`/activity-types/${id}`);
  },

  // Create activity type
  create: async (data: CreateActivityTypeDto): Promise<ActivityType> => {
    return api.post<ActivityType>('/activity-types', data);
  },

  // Update activity type
  update: async (id: string | number, data: UpdateActivityTypeDto): Promise<ActivityType> => {
    return api.patch<ActivityType>(`/activity-types/${id}`, data);
  },

  // Delete activity type
  delete: async (id: string | number): Promise<void> => {
    await api.delete(`/activity-types/${id}`);
  },
};
